import {
  Type, AlignLeft, Image as ImageIcon, MousePointerClick, Minus, MoveVertical,
  Share2, Video, Code, Columns, Square, Rows, LayoutTemplate,
} from "lucide-react";

const uid = () => `blk-${Math.random().toString(36).slice(2, 10)}`;
export const rowId = () => `row-${Math.random().toString(36).slice(2, 10)}`;

// Object-storage uploads return a same-origin relative path (e.g.
// /api/public/marketing-assets/...). Email HTML has no base URL to resolve
// relative paths against, so every image src embedded in a sent email must
// be made absolute before it's saved/rendered.
export const absUrl = (u) => (!u ? u : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

export const MERGE_TAGS = ["firstName", "lastName", "customerName", "companyName", "lastServiceDate", "reviewLink"];

// Full merge tag catalog for the Paragraph block's rich-text editor + the
// subject line — grouped for the "insert tag" panel UI. NOTE: this is
// intentionally separate from the flat MERGE_TAGS above (still used as-is by
// the Headline/Button block quick-chips) so those other blocks are unaffected.
export const MERGE_TAG_GROUPS = [
  { label: "Customer", tags: ["firstName", "lastName", "customerName", "customerEmail", "customerPhone", "customerAddress"] },
  { label: "Company", tags: ["companyName", "companyPhone", "companyEmail", "companyAddress", "companyWebsite"] },
  { label: "Job", tags: ["jobScheduledDate", "jobScheduledTime", "jobType", "jobStatus", "jobAddress", "jobNotes", "assignedTechnician", "technicianPhone"] },
  { label: "Estimate", tags: ["estimateNumber", "estimateTotal", "estimateStatus", "estimateLink"] },
  { label: "Invoice", tags: ["invoiceNumber", "invoiceTotal", "invoiceDueDate", "invoiceLink", "paymentLink"] },
  { label: "Other", tags: ["lastServiceDate", "reviewLink", "unsubscribeLink"] },
];

// ---- Palette definitions ----
export const BLOCK_DEFS = [
  { type: "headline", label: "Headline", icon: Type },
  { type: "paragraph", label: "Paragraph", icon: AlignLeft },
  { type: "image", label: "Image", icon: ImageIcon },
  { type: "button", label: "Button", icon: MousePointerClick },
  { type: "divider", label: "Divider", icon: Minus },
  { type: "spacer", label: "Spacer", icon: MoveVertical },
  { type: "social", label: "Social", icon: Share2 },
  { type: "video", label: "Video", icon: Video },
  { type: "html", label: "Custom HTML", icon: Code },
];

export const LAYOUTS = [
  { cols: 1, label: "1 column", icon: Square },
  { cols: 2, label: "2 columns", icon: Columns },
  { cols: 3, label: "3 columns", icon: Rows },
];

const SOCIAL_META = {
  facebook: { c: "#1877F2", t: "f" },
  instagram: { c: "#E4405F", t: "ig" },
  x: { c: "#000000", t: "X" },
  linkedin: { c: "#0A66C2", t: "in" },
  youtube: { c: "#FF0000", t: "▶" },
};

export function makeBlock(type, accent = "#2563EB") {
  const id = uid();
  switch (type) {
    case "headline": return { id, type, text: "Your headline goes here", align: "left", color: "#0f172a", fontSize: 26, bold: true };
    case "paragraph": return { id, type, html: "<p>Write your message here. Use merge tags like {{firstName}} to personalize.</p>", align: "left", color: "#334155", fontSize: 15 };
    case "image": return { id, type, src: "", alt: "", href: "", width: 100, align: "center" };
    case "button": return { id, type, text: "Shop Now", href: "https://", bgColor: accent, color: "#ffffff", align: "center", radius: 8 };
    case "divider": return { id, type, color: "#e2e8f0", thickness: 1, padding: 12 };
    case "spacer": return { id, type, height: 24 };
    case "social": return { id, type, links: [{ network: "facebook", url: "" }, { network: "instagram", url: "" }, { network: "x", url: "" }], align: "center", size: 36 };
    case "video": return { id, type, thumbnailSrc: "", href: "https://", alt: "Watch the video" };
    case "html": return { id, type, code: "<p style='text-align:center'>Your custom HTML here</p>" };
    default: return { id, type: "paragraph", html: "<p>Text</p>", align: "left", color: "#334155", fontSize: 15 };
  }
}

export function makeRow(cols = 1) {
  return { id: rowId(), cols, bg: "", columns: Array.from({ length: cols }, () => []) };
}

export function defaultDesign(accent = "#2563EB") {
  const r = makeRow(1);
  r.columns[0] = [
    { ...makeBlock("headline", accent), text: "Hi {{firstName}}, big news!" },
    makeBlock("paragraph", accent),
    makeBlock("button", accent),
  ];
  return {
    settings: { backgroundColor: "#f1f5f9", contentBackgroundColor: "#ffffff", contentWidth: 600, preheader: "", fontFamily: "Helvetica, Arial, sans-serif" },
    rows: [r],
  };
}

const esc = (s) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// ---- HTML renderer (email-safe, table-based) ----
export function renderBlock(b) {
  switch (b.type) {
    case "headline":
      return `<div style="font-size:${b.fontSize}px;font-weight:${b.bold ? 700 : 400};color:${b.color};text-align:${b.align};line-height:1.25;padding:6px 0;">${esc(b.text)}</div>`;
    case "paragraph":
      return `<div style="font-size:${b.fontSize}px;color:${b.color};text-align:${b.align};line-height:1.6;padding:4px 0;">${b.html || ""}</div>`;
    case "image": {
      const img = `<img src="${esc(b.src) || "https://via.placeholder.com/600x240?text=Image"}" alt="${esc(b.alt)}" width="${b.width}%" style="display:inline-block;width:${b.width}%;max-width:100%;border:0;outline:none;border-radius:6px;" />`;
      const inner = b.href ? `<a href="${esc(b.href)}" target="_blank">${img}</a>` : img;
      return `<div style="text-align:${b.align};padding:6px 0;">${inner}</div>`;
    }
    case "button":
      return `<div style="text-align:${b.align};padding:8px 0;"><a href="${esc(b.href)}" target="_blank" style="display:inline-block;background:${b.bgColor};color:${b.color};text-decoration:none;font-weight:600;font-size:15px;padding:12px 26px;border-radius:${b.radius}px;">${esc(b.text)}</a></div>`;
    case "divider":
      return `<div style="padding:${b.padding}px 0;"><div style="border-top:${b.thickness}px solid ${b.color};font-size:0;line-height:0;">&nbsp;</div></div>`;
    case "spacer":
      return `<div style="height:${b.height}px;line-height:${b.height}px;font-size:0;">&nbsp;</div>`;
    case "social": {
      const icons = (b.links || []).filter((l) => l.network).map((l) => {
        const m = SOCIAL_META[l.network] || { c: "#64748b", t: "?" };
        return `<a href="${esc(l.url) || "#"}" target="_blank" style="display:inline-block;width:${b.size}px;height:${b.size}px;line-height:${b.size}px;text-align:center;background:${m.c};color:#fff;border-radius:50%;font-size:13px;font-weight:700;text-decoration:none;margin:0 4px;">${m.t}</a>`;
      }).join("");
      return `<div style="text-align:${b.align};padding:8px 0;">${icons}</div>`;
    }
    case "video": {
      const thumb = esc(b.thumbnailSrc) || "https://via.placeholder.com/600x300?text=Video";
      return `<div style="text-align:center;padding:6px 0;"><a href="${esc(b.href)}" target="_blank" style="display:inline-block;position:relative;"><img src="${thumb}" alt="${esc(b.alt)}" width="100%" style="width:100%;max-width:100%;border-radius:6px;display:block;" /><span style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:rgba(0,0,0,0.55);color:#fff;width:54px;height:54px;line-height:54px;border-radius:50%;font-size:20px;">▶</span></a></div>`;
    }
    case "html":
      return `<div style="padding:4px 0;">${b.code || ""}</div>`;
    default:
      return "";
  }
}

export function renderRow(row) {
  const w = (100 / row.cols).toFixed(4);
  const cells = row.columns.map((col) =>
    `<td valign="top" width="${w}%" style="padding:6px 10px;">${(col || []).map(renderBlock).join("")}</td>`
  ).join("");
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${row.bg || "transparent"};"><tr>${cells}</tr></table>`;
}

export function renderEmailHtml(design) {
  const s = design.settings || {};
  const pre = s.preheader ? `<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent;">${esc(s.preheader)}</div>` : "";
  const rows = (design.rows || []).map(renderRow).join("");
  const width = s.contentWidth || 600;
  return `<div style="background:${s.backgroundColor || "#f1f5f9"};padding:24px 8px;font-family:${s.fontFamily || "Helvetica, Arial, sans-serif"};">${pre}` +
    `<table role="presentation" align="center" width="${width}" cellpadding="0" cellspacing="0" style="width:100%;max-width:${width}px;margin:0 auto;background:${s.contentBackgroundColor || "#ffffff"};border-radius:10px;overflow:hidden;">` +
    `<tr><td style="padding:18px 14px;">${rows}` +
    `<div style="padding:16px 8px 4px;margin-top:8px;border-top:1px solid #eef2f7;font-size:11px;color:#94a3b8;text-align:center;">Sent by {{companyName}}.</div>` +
    `</td></tr></table></div>`;
}

// Fill sample values for previews (so {{firstName}} reads naturally).
export function fillSample(html) {
  const sample = { firstName: "Alex", lastName: "Sample", customerName: "Alex Sample", companyName: "Your Company", lastServiceDate: "Jan 1, 2026", reviewLink: "#" };
  let out = html;
  MERGE_TAGS.forEach((k) => { out = out.split(`{{${k}}}`).join(sample[k] || ""); });
  return out;
}

// ---- Starter templates (as editable designs) ----
export function getTemplates(accent = "#2563EB") {
  const base = () => ({ backgroundColor: "#f1f5f9", contentBackgroundColor: "#ffffff", contentWidth: 600, preheader: "", fontFamily: "Helvetica, Arial, sans-serif" });
  const row = (blocks) => { const r = makeRow(1); r.columns[0] = blocks; return r; };
  const hl = (text) => ({ ...makeBlock("headline", accent), text });
  const p = (html) => ({ ...makeBlock("paragraph", accent), html });
  const btn = (text, href) => ({ ...makeBlock("button", accent), text, href });
  return [
    {
      id: "blank", name: "Blank", subject: "",
      design: { settings: base(), rows: [makeRow(1)] },
    },
    {
      id: "seasonal", name: "Seasonal promo", subject: "{{firstName}}, a seasonal offer just for you",
      design: { settings: base(), rows: [row([
        hl("A seasonal offer, {{firstName}}!"),
        p("<p>The season is changing — the perfect time for a tune-up. For a limited time, <strong>{{companyName}}</strong> is offering a special discount on all service visits.</p>"),
        btn("Book your visit", "https://"),
      ])] },
    },
    {
      id: "maintenance", name: "Maintenance reminder", subject: "Time for your maintenance check, {{firstName}}",
      design: { settings: base(), rows: [row([
        hl("Time for a check-up"),
        p("<p>Hi {{firstName}}, it's been a while since your last service (<strong>{{lastServiceDate}}</strong>). Regular maintenance keeps everything running efficiently.</p>"),
        btn("Schedule service", "https://"),
      ])] },
    },
    {
      id: "review", name: "Review request", subject: "How did we do, {{firstName}}?",
      design: { settings: base(), rows: [row([
        hl("How did we do?"),
        p("<p>Thank you for choosing <strong>{{companyName}}</strong>! We'd love to hear about your experience.</p>"),
        btn("Leave a review", "{{reviewLink}}"),
      ])] },
    },
    {
      id: "winback", name: "We miss you", subject: "We miss you, {{firstName}}",
      design: { settings: base(), rows: [row([
        hl("We miss you, {{firstName}}"),
        p("<p>It's been a while since we last served you. As a thank-you, <strong>{{companyName}}</strong> would like to offer you a special rate on your next visit.</p>"),
        btn("Claim your offer", "https://"),
      ])] },
    },
  ];
}

// ---- Sections library (pre-designed row groups) ----
export function getSections(accent = "#2563EB") {
  const oneRow = (blocks) => { const r = makeRow(1); r.columns[0] = blocks; return r; };
  return [
    {
      id: "hero", name: "Hero", icon: LayoutTemplate, rows: () => [oneRow([
        { ...makeBlock("headline", accent), text: "Sales are in the air!", fontSize: 30, align: "center" },
        { ...makeBlock("paragraph", accent), html: "<p style='text-align:center'>Refresh your wardrobe with 40% off!</p>", align: "center" },
        { ...makeBlock("button", accent), text: "Shop Now", align: "center" },
      ])],
    },
    {
      id: "header", name: "Header + logo", icon: ImageIcon, rows: () => [oneRow([
        { ...makeBlock("image", accent), width: 40, align: "center" },
        makeBlock("divider", accent),
      ])],
    },
    {
      id: "two-feature", name: "2-column feature", icon: Columns, rows: () => {
        const r = makeRow(2);
        r.columns[0] = [makeBlock("image", accent), { ...makeBlock("paragraph", accent), html: "<p>Feature one description.</p>", align: "center" }];
        r.columns[1] = [makeBlock("image", accent), { ...makeBlock("paragraph", accent), html: "<p>Feature two description.</p>", align: "center" }];
        return [r];
      },
    },
    {
      id: "cta", name: "Call to action", icon: MousePointerClick, rows: () => [oneRow([
        { ...makeBlock("headline", accent), text: "Ready to get started?", align: "center" },
        { ...makeBlock("button", accent), text: "Get in touch", align: "center" },
      ])],
    },
    {
      id: "footer", name: "Footer", icon: Rows, rows: () => [oneRow([
        makeBlock("divider", accent),
        { ...makeBlock("paragraph", accent), html: "<p style='text-align:center;font-size:12px;color:#94a3b8'>You're receiving this because you're a customer of {{companyName}}.</p>", align: "center", fontSize: 12, color: "#94a3b8" },
        makeBlock("social", accent),
      ])],
    },
  ];
}
